/* eslint-disable max-lines-per-function, complexity, max-len, no-console, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars, no-unused-vars, jsx-a11y/label-has-associated-control, react/no-unescaped-entities, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import {
  getProdSupabaseClient,
  supabase,
  useAuth,
  useToast
} from '@vehix/shared/admin/portal-bridge';
import { createClient } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@vehix/portal/components/ui/table';
import { Button } from '@vehix/portal/components/ui/button';
import { Badge } from '@vehix/portal/components/ui/badge';
import { Input } from '@vehix/portal/components/ui/input';
import { Textarea } from '@vehix/portal/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@vehix/portal/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@vehix/portal/components/ui/select';
import {
  CheckCircle,
  XCircle,
  Search,
  Shield,
  Mail,
  Calendar,
  User as UserIcon,
  UserPlus,
  TrendingUp,
  RefreshCcw,
  Trash2,
  FileText,
  Users,
  AlertCircle,
  Building2,
  MoreVertical,
  Key,
  Ban,
  Loader2
} from 'lucide-react';
import { UserAuditTab } from './UserAuditTab';
import { StatCard } from './dashboard/StatCard';
import { Tabs, TabsList, TabsTrigger } from '@vehix/portal/components/ui/tabs';
import { tenantAPI, Tenant } from '@vehix/portal/services/api/tenants';
import { useAllTenants } from '@vehix/portal/hooks/useTenants';
import { SystemRole } from '@vehix/portal/types';
import { getPayingCustomerIds } from '../utils/payingCustomers';
import { normalizeEmail } from '../utils/emailIdentity';
import { AddUserDialog, type NewUserFormValues } from './AddUserDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@vehix/portal/components/ui/dropdown-menu';
import { validateTenantSetup } from '@vehix/portal/lib/supabase/diagnostics';

export interface UserApprovalRecord {
  id: string;
  user_id: string;
  email: string;
  full_name: string | null;
  first_name?: string | null;
  last_name?: string | null;
  auth_provider: string;
  status: 'pending' | 'approved' | 'rejected';
  requested_at: string;
  approved_at: string | null;
  approved_by: string | null;
  approved_by_name: string | null;
  notes: string | null;
  // Returned by get_all_user_approvals_for_admin. Was dropped from this
  // interface during the portal -> shared extraction, which silently
  // discarded it; restoring it is what makes domain -> tenant inference
  // possible without a second round trip.
  // `| undefined` is explicit because exactOptionalPropertyTypes is on and
  // the tenant-switch rollback assigns this field a possibly-undefined value.
  tenant_id?: string | null | undefined;
  last_active_at?: string | null;
  // Auth user data
  email_confirmed_at?: string | null;
  last_sign_in_at?: string | null;
  invited_at?: string | null;
  user_role?: {
    role_id: string;
    role_name: string;
    role_display_name: string;
  } | null;
  user_tenants?: Tenant[];
}

export interface Role {
  id: string;
  name: string;
  display_name: string;
  description: string | null;
  system_role: string;
  priority: number;
}

export const UserApprovalsTab: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [approvals, setApprovals] = useState<UserApprovalRecord[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  // Top-level user lifecycle tab. Replaces the previous raw-status Select
  // ('pending' / 'approved' / 'rejected'). Operational categories admins
  // actually scan in: All | Active (signed in at least once) | Pending
  // Sign-up (approved but never confirmed email) | Inactive (rejected
  // or banned). Defaults to 'active' so the working set lands first.
  type UserTab = 'all' | 'active' | 'pending-signup' | 'inactive';
  const [userTab, setUserTab] = useState<UserTab>('active');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [tenantFilter, setTenantFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'users' | 'audit'>('users');
  const [selectedApproval, setSelectedApproval] = useState<UserApprovalRecord | null>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject'>('approve');
  const [actionNotes, setActionNotes] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [userToRemove, setUserToRemove] = useState<UserApprovalRecord | null>(null);

  // Confirmation-gate state for inline edit actions. Each pending* slot
  // captures the args of an attempted edit; the matching dialog displays
  // the change and only on Confirm does the existing handle* function
  // fire. Cancel clears the slot; the underlying Select snaps back to
  // the canonical value automatically since it's controlled by state.
  const [pendingRoleChange, setPendingRoleChange] = useState<{
    approval: UserApprovalRecord;
    newRoleId: string;
    oldRoleName: string;
    newRoleName: string;
  } | null>(null);
  const [pendingTenantSwitch, setPendingTenantSwitch] = useState<{
    approval: UserApprovalRecord;
    newTenantId: string;
    oldTenantName: string;
    newTenantName: string;
  } | null>(null);
  const [pendingResendInvite, setPendingResendInvite] = useState<UserApprovalRecord | null>(null);
  const [pendingResetPassword, setPendingResetPassword] = useState<UserApprovalRecord | null>(null);
  const [confirmActionLoading, setConfirmActionLoading] = useState(false);
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  // Non-fatal role-list failure. loadRoles used to swallow these, leaving the
  // dropdown stuck on "Loading roles..." and Create permanently disabled.
  const [rolesError, setRolesError] = useState<string | null>(null);

  // Get all available tenants for selection (admin mode - bypasses RLS)
  const {
    data: allTenants,
    isLoading: allTenantsLoading,
    error: allTenantsError
  } = useAllTenants();

  // Tenant setup validation state
  const [tenantSetupValidated, setTenantSetupValidated] = useState(false);
  const [_tenantSetupInfo, setTenantSetupInfo] = useState<{
    prodTenantsExists: boolean;
    prodTenantsCount: number;
    userTenantsExists: boolean;
    userTenantsCount: number;
  } | null>(null);

  // Pagination state — defaults to 100 per page so most lists fit one page
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  // Use regular prod client with RLS (super_admin users should have access via RLS policies)
  // Note: This page is only accessible to super_admin users, so RLS should allow access
  const getAdminProdClient = () => {
    return getProdSupabaseClient();
  };

  // For auth operations, we still need service role key to create users
  // This is a legitimate use case for service role key in browser (admin-only page)
  const getAdminAuthClient = () => {
    const supabaseUrl =
      import.meta.env.VITE_SUPABASE_URL || 'https://exycbmlolhxpacnyqvmw.supabase.co';
    const serviceRoleKey =
      import.meta.env['VITE_SUPABASE_SERVICE_ROLE_KEY'] ||
      import.meta.env['SUPABASE_SERVICE_ROLE_KEY'];

    if (!serviceRoleKey) {
      // Fallback to regular client - user creation might fail but other operations will work
      console.warn('Service role key not available. User creation operations may fail.');
      return supabase;
    }

    return createClient(supabaseUrl, serviceRoleKey, {
      auth: {
        autoRefreshToken: false,
        persistSession: false
      }
    });
  };

  // Load approvals
  const loadApprovals = async () => {
    try {
      setLoading(true);

      // Use RPC function to get all user approvals (bypasses RLS for super admins)
      // This follows the SECURITY DEFINER pattern for admin operations
      // IMPORTANT: Use regular supabase client (not prod client) for RPC calls
      // PostgREST searches public schema by default for RPC calls
      let approvalsData: any[] | null = null;
      let approvalsError: any = null;

      const rpcResult = await supabase.rpc('get_all_user_approvals_for_admin');
      approvalsData = rpcResult.data;
      approvalsError = rpcResult.error;

      if (approvalsError) {
        // Fallback to direct query if RPC function doesn't exist yet
        console.warn(
          '[UserApprovalsTab] RPC function not available, falling back to direct query:',
          approvalsError
        );
        const adminClient = getAdminProdClient();
        const result = await adminClient
          .from('user_approvals')
          .select(
            'id, user_id, email, full_name, auth_provider, status, requested_at, approved_at, approved_by, approved_by_name, notes'
          )
          .order('requested_at', { ascending: false });

        if (result.error) {
          throw result.error;
        }
        // Use fallback data (but this will still respect RLS)
        approvalsData = result.data || [];
      }

      // Batch fetch all user auth data via RPC (avoids N+1 queries and doesn't need service role key)
      const userIds = (approvalsData || []).map(a => a.user_id);
      let authDataMap: Record<string, any> = {};

      if (userIds.length > 0) {
        const { data: authDataArray, error: authDataError } = await supabase.rpc(
          'get_users_auth_data_for_admin',
          { p_user_ids: userIds }
        );

        if (authDataError) {
          console.warn('[UserApprovalsTab] Error fetching batch auth data:', authDataError);
        } else if (authDataArray) {
          // Create a map for quick lookup
          authDataMap = (authDataArray as any[]).reduce(
            (acc, user) => {
              acc[user.user_id] = user;
              return acc;
            },
            {} as Record<string, any>
          );
        }
      }

      // For each approval, use role data from RPC and fetch tenants
      const approvalsWithRoles = await Promise.all(
        (approvalsData || []).map(async approval => {
          // Role data is already included in the RPC result
          let userRole = null;
          if (approval.role_id && approval.role_name) {
            userRole = {
              role_id: approval.role_id,
              role_name: approval.role_name,
              role_display_name: approval.role_display_name || approval.role_name
            };
          }
          let userTenantsList: Tenant[] = [];
          let emailConfirmedAt: string | null = null;
          let lastSignInAt: string | null = null;
          let invitedAt: string | null = null;
          let firstName: string | null = null;
          let lastName: string | null = null;

          // Use pre-fetched auth data from RPC
          const authUser = authDataMap[approval.user_id];
          if (authUser) {
            emailConfirmedAt = authUser.email_confirmed_at || null;
            lastSignInAt = authUser.last_sign_in_at || null;
            invitedAt = authUser.invited_at || null;
            firstName = authUser.first_name || null;
            lastName = authUser.last_name || null;

            // If not in metadata, try to parse from full_name
            if (!firstName && !lastName && authUser.full_name) {
              const nameParts = authUser.full_name.trim().split(/\s+/);
              firstName = nameParts[0] || null;
              lastName = nameParts.slice(1).join(' ') || null;
            }
          }

          if (approval.status === 'approved') {
            // Fetch tenants using admin client to bypass RLS
            try {
              userTenantsList = await tenantAPI.getUserTenants(approval.user_id, true); // Use admin client
              if (userTenantsList && userTenantsList.length > 0) {
                console.log(
                  `[UserApprovalsTab] Loaded ${userTenantsList.length} tenants for user ${approval.user_id}:`,
                  userTenantsList.map(t => t.name || t.tenant_name)
                );
              }
            } catch (error) {
              console.error(
                `[UserApprovalsTab] Error loading tenants for user ${approval.user_id}:`,
                error
              );
              // Silently handle tenant loading errors to prevent breaking the approval list
            }
          }

          return {
            ...approval,
            requested_at: approval.created_at || approval.requested_at || new Date().toISOString(), // RPC returns created_at
            first_name: firstName || approval.full_name?.split(' ')[0] || null,
            last_name: lastName || approval.full_name?.split(' ').slice(1).join(' ') || null,
            email_confirmed_at: emailConfirmedAt,
            last_sign_in_at: lastSignInAt,
            invited_at: invitedAt,
            user_role: userRole,
            user_tenants: userTenantsList
          };
        })
      );

      setApprovals(approvalsWithRoles);
    } catch (error) {
      toast({
        title: 'Error Loading Approvals',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to load user approvals. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Load available roles from prod.roles
  const loadRoles = async () => {
    try {
      const adminClient = getAdminProdClient();
      const { data, error } = await adminClient
        .from('roles')
        .select('id, name, display_name, description, level, scope')
        .order('level', { ascending: false });

      if (error) throw error;

      // Map to expected interface (add system_role and priority from existing fields)
      const rolesWithMapping = (data || []).map(role => ({
        ...role,
        system_role: role.name, // Use name as system_role
        priority: role.level // Use level as priority
      }));

      setRoles(rolesWithMapping);
      setRolesError(null);
    } catch (error) {
      // Previously swallowed. If RLS blocks prod.roles the dropdown sits on
      // "Loading roles..." forever and Create stays disabled with no reason
      // shown, which is indistinguishable from the page being broken.
      console.error('[UserApprovalsTab] Failed to load roles:', error);
      setRolesError(
        error instanceof Error ? error.message : 'Roles could not be loaded. Please refresh.'
      );
    }
  };

  // Validate tenant setup when component mounts or when tenant error occurs
  useEffect(() => {
    const checkTenantSetup = async () => {
      if (allTenantsError && !tenantSetupValidated) {
        try {
          const validation = await validateTenantSetup();
          setTenantSetupInfo(validation);
          setTenantSetupValidated(true);
        } catch (error) {
          // Silently handle validation errors
        }
      }
    };

    checkTenantSetup();
  }, [allTenantsError, tenantSetupValidated]);

  useEffect(() => {
    if (viewMode === 'users') {
      loadApprovals();
    }
    loadRoles();
  }, [viewMode]);

  // Filter approvals.
  //
  // Memoized on its real inputs. Unmemoized, this re-filtered the entire
  // approvals array on EVERY render — including every keystroke in the Add
  // User dialog — and returned a fresh array each time, which also forced the
  // kpiMetrics memo below to recompute. Typing now costs nothing here.
  const filteredApprovals = useMemo(() => {
    const search = searchTerm.toLowerCase();

    return approvals.filter(approval => {
      const matchesSearch =
        !search ||
        approval.email.toLowerCase().includes(search) ||
        (approval.full_name?.toLowerCase() || '').includes(search);

      // User-lifecycle tab predicate. Mirrors the categories the admins
      // actually think in (not raw DB statuses).
      const matchesTab =
        userTab === 'all' ||
        (userTab === 'active' && !!approval.last_sign_in_at) ||
        (userTab === 'pending-signup' &&
          approval.status === 'approved' &&
          !approval.email_confirmed_at) ||
        (userTab === 'inactive' &&
          (approval.status === 'rejected' ||
            !!(approval as { banned_until?: string | null }).banned_until));

      const matchesRole =
        roleFilter === 'all' ||
        (roleFilter === 'no_role' && !approval.user_role) ||
        approval.user_role?.role_id === roleFilter;

      const matchesTenant =
        tenantFilter === 'all' || approval.user_tenants?.some(tenant => tenant.id === tenantFilter);

      return matchesSearch && matchesTab && matchesRole && matchesTenant;
    });
  }, [approvals, searchTerm, userTab, roleFilter, tenantFilter]);

  // KPI metrics — react to filters (Total Users, Admins, Pending Sign-up
  // are sourced from filteredApprovals so the cards mirror what the table
  // is showing). Avg Users/Tenant uses the global paying-customer count
  // as denominator so the metric stays stable as filters narrow rows.
  const kpiMetrics = React.useMemo(() => {
    let totalUsers = 0;
    let totalAdmins = 0;
    let pendingSignup = 0;

    filteredApprovals.forEach(a => {
      // role_name comes from the DB as the snake_case enum value
      // ('super_admin'), not the TypeScript-style identifier
      // ('SUPER_ADMIN'). Use the SystemRole enum so future renames flow
      // through automatically.
      const isAdmin = a.user_role?.role_name === SystemRole.SUPER_ADMIN;
      if (isAdmin) {
        totalAdmins += 1;
      }

      if (a.status === 'approved' && !isAdmin) {
        totalUsers += 1;
      }

      // Pending sign-up: approved (invite sent) but never confirmed email/set password
      if (a.status === 'approved' && !a.email_confirmed_at) {
        pendingSignup += 1;
      }
    });

    // Avg Users/Tenant: denominator is the global paying-customer count
    // (matches the Tenant Setup definition exactly). Numerator is the
    // filtered user count; ratio is meaningful even when filters narrow
    // the user set.
    const payingTenantCount = getPayingCustomerIds(allTenants).size;
    const avgUsersPerTenant =
      payingTenantCount > 0 ? Math.round((totalUsers / payingTenantCount) * 10) / 10 : 0;

    return { totalUsers, totalAdmins, pendingSignup, avgUsersPerTenant };
  }, [filteredApprovals, allTenants]);

  // Per-tab counts shown next to tab labels. Derived from the FULL
  // approvals list (not filteredApprovals) so each count reflects the
  // total in that lifecycle stage, regardless of search/role/tenant
  // filters. Single pass for all four counts.
  const tabCounts = React.useMemo(() => {
    let active = 0;
    let pendingSignup = 0;
    let inactive = 0;
    approvals.forEach(a => {
      if (a.last_sign_in_at) active += 1;
      if (a.status === 'approved' && !a.email_confirmed_at) pendingSignup += 1;
      if (a.status === 'rejected' || (a as { banned_until?: string | null }).banned_until) {
        inactive += 1;
      }
    });
    return { all: approvals.length, active, pendingSignup, inactive };
  }, [approvals]);

  // Pagination logic. Memoized so the page slice keeps a stable identity
  // across renders that don't touch the list — typing in the Add User dialog
  // no longer rebuilds this array on every character.
  const totalPages = Math.ceil(filteredApprovals.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedApprovals = useMemo(
    () => filteredApprovals.slice(startIndex, startIndex + pageSize),
    [filteredApprovals, startIndex, pageSize]
  );

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, userTab, roleFilter, tenantFilter]);

  // Handle approve/reject
  const handleAction = async () => {
    if (!selectedApproval || !user) return;

    // Validate role selection for approval
    if (actionType === 'approve' && !selectedRoleId) {
      toast({
        title: 'Role Required',
        description: 'Please select a role for the user before approving.',
        variant: 'destructive'
      });
      return;
    }

    try {
      // Update approval status via RPC (bypasses RLS)
      const approvalStatus = actionType === 'approve' ? 'approved' : 'rejected';
      const { data: approvalResult, error: approvalError } = await supabase.rpc(
        'update_user_approval_for_admin',
        {
          p_user_id: selectedApproval.user_id,
          p_status: approvalStatus,
          p_approved_by: actionType === 'approve' ? user.id : null,
          p_approved_by_name:
            actionType === 'approve' ? user.email?.split('@')[0] || 'Admin' : null,
          p_notes: actionNotes || null
        }
      );

      if (approvalError) throw approvalError;

      if (approvalResult && !approvalResult['success']) {
        throw new Error(approvalResult['error'] || 'Failed to update approval');
      }

      // If approving, assign the selected role to the user via RPC
      if (actionType === 'approve' && selectedRoleId) {
        const tenantId = selectedApproval.tenant_id;

        if (!tenantId) {
          throw new Error(
            'User must have a tenant assigned in their approval record before approving'
          );
        }

        const { error: roleError } = await supabase.rpc('assign_user_role', {
          p_user_id: selectedApproval.user_id,
          p_tenant_id: tenantId,
          p_role_id: selectedRoleId,
          p_assigned_by: user.id
        });

        if (roleError) throw roleError;
      }

      toast({
        title: actionType === 'approve' ? 'User Approved' : 'User Rejected',
        description: `${selectedApproval.email} has been ${actionType === 'approve' ? 'approved' : 'rejected'}.`,
        variant: 'success'
      });

      // Note: Audit log for user_approval actions is automatically created by trigger
      // prod.trg_user_approvals_audit() which calls prod.write_audit_log()
      // So we don't need to manually insert for approve/reject actions
      // The trigger will handle: user_approval_approved, user_approval_rejected, etc.

      // Refresh list
      await loadApprovals();

      // Close dialog
      setActionDialogOpen(false);
      setSelectedApproval(null);
      setActionNotes('');
      setSelectedRoleId('');
    } catch (error) {
      toast({
        title: 'Action Failed',
        description: 'Failed to update user approval. Please try again.',
        variant: 'destructive'
      });
    }
  };

  // Handle role change for approved users
  const handleRoleChange = async (approval: UserApprovalRecord, newRoleId: string) => {
    if (!user) return;

    try {
      // Get tenant_id from the approval record (source of truth for JWT)
      const tenantId = approval.tenant_id;

      if (!tenantId) {
        toast({
          title: 'Error',
          description:
            'User must have a tenant assigned in their approval record before changing role.',
          variant: 'destructive'
        });
        return;
      }

      // Use assign_user_role RPC (SECURITY DEFINER, bypasses RLS)
      // This atomically: deactivates old roles for user+tenant, inserts new role
      const { error: roleError } = await supabase.rpc('assign_user_role', {
        p_user_id: approval.user_id,
        p_tenant_id: tenantId,
        p_role_id: newRoleId,
        p_assigned_by: user.id
      });

      if (roleError) throw roleError;

      toast({
        title: 'Role Updated',
        description: `Role changed successfully for ${approval.email}`,
        variant: 'success'
      });

      // Log audit event via RPC
      try {
        const newRole = roles.find(r => r.id === newRoleId);

        await supabase.rpc('create_audit_log', {
          p_user_id: user.id,
          p_tenant_id: tenantId,
          p_action: 'user.role_changed',
          p_resource_type: 'user_role',
          p_resource_id: approval.user_id,
          p_old_values: {
            role_id: approval.user_role?.role_id,
            role_name: approval.user_role?.role_display_name,
            email: approval.email,
            full_name: approval.full_name
          },
          p_new_values: {
            role_id: newRoleId,
            role_name: newRole?.display_name,
            email: approval.email,
            full_name: approval.full_name,
            changed_from_ui: true
          }
        });
      } catch (auditError) {
        // Don't fail the operation if audit logging fails
      }

      // Refresh the list
      await loadApprovals();
    } catch (error) {
      toast({
        title: 'Update Failed',
        description: 'Failed to update user role. Please try again.',
        variant: 'destructive'
      });
    }
  };

  // Open action dialog
  const openActionDialog = (approval: UserApprovalRecord, type: 'approve' | 'reject') => {
    setSelectedApproval(approval);
    setActionType(type);
    setActionDialogOpen(true);
  };

  // Open remove dialog
  const openRemoveDialog = (approval: UserApprovalRecord) => {
    setUserToRemove(approval);
    setRemoveDialogOpen(true);
  };

  // Handle tenant switch (single tenant only: replace current tenant with selected one)
  const handleTenantSwitch = async (approval: UserApprovalRecord, newTenantId: string) => {
    if (!user) return;

    const userId = approval.user_id;
    const currentTenantId = approval.tenant_id || approval.user_tenants?.[0]?.id;

    // No-op if already assigned to this tenant
    if (currentTenantId === newTenantId) return;

    const newTenant = allTenants?.find(t => t.id === newTenantId);
    const newTenantName = newTenant?.name || newTenant?.tenant_name || newTenantId;

    // Store original state for rollback
    const originalTenants = approval.user_tenants || [];
    const originalTenantId = approval.tenant_id;

    // Optimistic update: set user_tenants to [newTenant] and tenant_id to newTenantId
    const tenantForOptimistic = newTenant || {
      id: newTenantId,
      tenant_name: newTenantId,
      name: newTenantId
    };
    setApprovals(prev =>
      prev.map(a =>
        a.user_id !== userId
          ? a
          : { ...a, user_tenants: [tenantForOptimistic], tenant_id: newTenantId }
      )
    );

    toast({
      title: 'Tenant Switched',
      description: `${approval.email} is now assigned to ${newTenantName}.`,
      variant: 'success'
    });

    try {
      // Remove user from all current tenants
      for (const t of approval.user_tenants || []) {
        try {
          await tenantAPI.removeUserTenant(userId, t.id);
        } catch (removeErr) {
          console.warn(`[handleTenantSwitch] Error removing tenant ${t.id}:`, removeErr);
        }
      }

      // Add user to the new tenant
      await tenantAPI.addUserTenant(userId, newTenantId);

      // Always sync user_approvals.tenant_id and user_roles.tenant_id
      const { error: updateError } = await supabase.rpc('update_user_approval_for_admin', {
        p_user_id: userId,
        p_tenant_id: newTenantId
      });

      if (updateError) {
        console.error('[handleTenantSwitch] Error updating primary tenant:', updateError);
        throw updateError;
      }

      // Audit log (non-blocking)
      supabase
        .rpc('create_audit_log', {
          p_user_id: user.id,
          p_tenant_id: newTenantId,
          p_action: 'user.tenant_switched',
          p_resource_type: 'user_tenant',
          p_resource_id: userId,
          p_old_values: {
            email: approval.email,
            full_name: approval.full_name,
            previous_tenant_id: originalTenantId
          },
          p_new_values: {
            tenant_id: newTenantId,
            tenant_name: newTenantName,
            email: approval.email,
            full_name: approval.full_name,
            changed_from_ui: true
          }
        })
        .then(() => {})
        .catch(auditErr =>
          console.warn('[handleTenantSwitch] Audit log failed (non-fatal):', auditErr)
        );

      queryClient.invalidateQueries({ queryKey: ['tenants', 'all'] });
      await loadApprovals();
    } catch (error) {
      console.error('[handleTenantSwitch] Error:', error);

      // Rollback optimistic update
      setApprovals(prev =>
        prev.map(a =>
          a.user_id !== userId
            ? a
            : { ...a, user_tenants: originalTenants, tenant_id: originalTenantId }
        )
      );

      toast({
        title: 'Update Failed',
        description:
          error instanceof Error ? error.message : 'Failed to switch tenant. Please try again.',
        variant: 'destructive'
      });
    }
  };

  // Helper function to create user records for existing users (single tenant only)
  const createUserRecordsForExistingUser = async (
    userId: string,
    email: string,
    firstName: string,
    lastName: string,
    roleId: string,
    tenantId: string,
    notes: string,
    approvedBy: string,
    adminProdClient: ReturnType<typeof getAdminProdClient>
  ) => {
    const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();

    // Create/update approval record.
    // onConflict MUST match a real unique index. prod.user_approvals is
    // UNIQUE (tenant_id, user_id) — there is no unique index on user_id
    // alone, so the previous 'user_id' target made Postgres reject this
    // upsert outright with 42P10 (no matching ON CONFLICT specification).
    const { error: approvalError } = await adminProdClient.from('user_approvals').upsert(
      {
        user_id: userId,
        email: normalizeEmail(email),
        full_name: fullName,
        auth_provider: 'email',
        status: 'approved',
        requested_at: new Date().toISOString(),
        approved_at: new Date().toISOString(),
        approved_by: approvedBy,
        approved_by_name: user?.email?.split('@')[0] || 'Admin',
        notes: notes || 'Created by admin via Control Center.',
        tenant_id: tenantId
      },
      {
        onConflict: 'tenant_id,user_id'
      }
    );

    if (approvalError) throw approvalError;

    // Assign role
    const { error: roleError } = await adminProdClient.from('user_roles').upsert(
      {
        user_id: userId,
        tenant_id: tenantId,
        role_id: roleId,
        assigned_by: approvedBy,
        is_active: true
      },
      {
        onConflict: 'user_id,tenant_id,role_id'
      }
    );

    if (roleError) throw roleError;

    // Assign the single tenant
    try {
      await tenantAPI.addUserTenant(userId, tenantId);
    } catch (tenantError) {
      console.warn(`Failed to assign tenant ${tenantId} to user ${userId}:`, tenantError);
    }

    // Create profile record - use check-then-insert/update to avoid 400 errors with composite unique constraints
    try {
      const { data: existingProfile } = await adminProdClient
        .from('user_profiles')
        .select('id')
        .eq('user_id', userId)
        .eq('tenant_id', tenantId)
        .maybeSingle();

      if (existingProfile) {
        // Update existing profile - only use columns that exist in prod.user_profiles
        // Actual columns: id, user_id, tenant_id, profile_photo_path, phone_number, job_title, department, created_at, updated_at
        await adminProdClient
          .from('user_profiles')
          .update({
            updated_at: new Date().toISOString()
          })
          .eq('user_id', userId)
          .eq('tenant_id', tenantId);
      } else {
        // Insert new profile - only use columns that exist in prod.user_profiles
        // Actual columns: id, user_id, tenant_id, profile_photo_path, phone_number, job_title, department, created_at, updated_at
        const profileData = {
          user_id: userId,
          tenant_id: tenantId
          // Other fields (profile_photo_path, phone_number, job_title, department) are optional
        };

        console.log('[createUserRecords] Inserting user profile with data:', profileData);
        const { data: profileInsertData, error: profileError } = await adminProdClient
          .from('user_profiles')
          .insert(profileData)
          .select();

        if (profileError) {
          console.error('[createUserRecords] Failed to create user profile record:', {
            error: profileError,
            message: profileError.message,
            details: profileError.details,
            hint: profileError.hint,
            code: profileError.code,
            data: profileData
          });
        } else {
          console.log('[createUserRecords] Successfully created user profile:', profileInsertData);
        }
      }
    } catch (profileError) {
      console.warn('Failed to create user profile record:', profileError);
    }
  };

  /**
   * Creates a user from the Add User dialog's values.
   *
   * Validation, duplicate detection and tenant inference all live in
   * AddUserDialog; the values arriving here are already normalized and
   * trimmed. Resolves true when the dialog should clear itself, false when
   * it should keep what the admin typed so they can correct and retry.
   */
  const handleCreateUser = async (values: NewUserFormValues): Promise<boolean> => {
    if (!user) return false;

    const {
      email: normalizedEmail,
      firstName: newUserFirstName,
      lastName: newUserLastName,
      roleId: newUserRoleId,
      tenantId: newUserTenantId,
      notes: newUserNotes
    } = values;

    // Combine first and last name for full_name
    const newUserFullName = `${newUserFirstName} ${newUserLastName}`.trim();

    try {
      setCreatingUser(true);

      // Get admin clients for auth and database operations
      const adminAuthClient = getAdminAuthClient();
      const adminProdClient = getAdminProdClient();

      // Step 1: Invite user via Supabase - this sends "Invite user" email template
      // This is the PRIMARY method for new user setup following Supabase's invite flow:
      // - Creates user in "invited" state
      // - Sends email with one-time invite link
      // - User clicks link → lands on /auth/set-password → exchanges code for session → sets password
      // Use window.location.origin like the working commit c487680f2adac55adcb54cd344ec996393cfb0ea
      const redirectUrl = `${window.location.origin}/auth/set-password`;

      console.log('[handleCreateUser] Calling inviteUserByEmail with redirectUrl:', redirectUrl);
      const { data: inviteData, error: inviteError } =
        await adminAuthClient.auth.admin.inviteUserByEmail(normalizedEmail, {
          data: {
            full_name: newUserFullName,
            first_name: newUserFirstName,
            last_name: newUserLastName
          },
          redirectTo: redirectUrl
        });

      console.log('[handleCreateUser] inviteUserByEmail response:', {
        hasData: !!inviteData,
        userId: inviteData?.user?.id,
        userEmail: inviteData?.user?.email,
        invitedAt: inviteData?.user?.invited_at,
        error: inviteError ? inviteError.message : null
      });

      // The auth user this run is operating on. Reassigned by the re-invite
      // branch below, which is why it can't be the const from the destructure.
      let resolvedAuthUser = inviteData?.user ?? null;

      if (inviteError) {
        console.log('[handleCreateUser] Invite error:', inviteError);
        console.log(
          '[handleCreateUser] Error code:',
          (inviteError as any)?.status || (inviteError as any)?.code
        );
        console.log('[handleCreateUser] Error message:', inviteError.message);

        // Check if user already exists - check both error code and message
        const errorCode = (inviteError as any)?.status || (inviteError as any)?.code;
        const errorMessage = inviteError.message?.toLowerCase() || '';
        const isExistingUser =
          errorCode === 'email_exists' ||
          errorCode === 422 ||
          errorMessage.includes('already registered') ||
          errorMessage.includes('already exists') ||
          errorMessage.includes('user with this email');

        // Check if this is a 500 error from trying to invite a previously deleted user
        // This happens when user was soft-deleted but still exists in auth.users
        const isEmailSendError =
          errorCode === 500 ||
          errorCode === 'unexpected_failure' ||
          errorMessage.includes('error sending invite email');

        if (isExistingUser || isEmailSendError) {
          console.log(
            '[handleCreateUser] User may already exist or was previously deleted, checking auth.users...'
          );
          // Check if user exists in auth.users
          const { data: usersData, error: listError } =
            await adminAuthClient.auth.admin.listUsers();
          if (listError) {
            console.error('[handleCreateUser] Error listing users:', listError);
            throw listError;
          }

          const existingUser = usersData?.users?.find(
            u => u.email?.toLowerCase() === normalizedEmail
          );

          if (existingUser) {
            console.log('[handleCreateUser] Found existing user:', existingUser.id);

            // Deleting an auth user is DESTRUCTIVE: prod.user_approvals,
            // user_roles, user_tenants, user_profiles, user_preferences and
            // user_activity_log all cascade from auth.users(id), so the row
            // and its whole history disappear.
            //
            // This used to fire whenever `!existingUser.email_confirmed_at`,
            // which is true for every user who simply hasn't accepted their
            // invite yet — the most common re-add case there is. Restricted
            // to genuinely unusable accounts: a banned user, or a broken
            // auth row that makes the invite email fail to send.
            const userIsBanned = existingUser.banned_until !== null;
            const userNeedsReactivation = isEmailSendError || userIsBanned;

            if (userNeedsReactivation) {
              console.log(
                '[handleCreateUser] User needs reactivation, deleting and re-creating...'
              );

              // Delete the existing user from auth
              const { error: deleteError } = await adminAuthClient.auth.admin.deleteUser(
                existingUser.id
              );
              if (deleteError) {
                console.error('[handleCreateUser] Error deleting existing user:', deleteError);
                throw new Error(
                  `Failed to reset user account: ${deleteError.message}. Please try again or contact support.`
                );
              }

              console.log('[handleCreateUser] Deleted existing user, sending fresh invite...');

              // Now re-invite the user (recursively call the invite again)
              const { data: reInviteData, error: reInviteError } =
                await adminAuthClient.auth.admin.inviteUserByEmail(normalizedEmail, {
                  data: {
                    full_name: newUserFullName,
                    first_name: newUserFirstName,
                    last_name: newUserLastName
                  },
                  redirectTo: redirectUrl
                });

              if (reInviteError) {
                console.error('[handleCreateUser] Re-invite error:', reInviteError);
                throw new Error(
                  `Failed to send invitation after account reset: ${reInviteError.message}`
                );
              }

              // Continue with the new user flow using the re-invited user
              if (reInviteData?.user?.id) {
                console.log(
                  '[handleCreateUser] User re-invited successfully:',
                  reInviteData.user.id
                );
                // `inviteData` is null whenever inviteError fired, so the old
                // Object.assign(inviteData || {}, ...) mutated a throwaway
                // object and the new id was lost. Track it in a local instead.
                resolvedAuthUser = reInviteData.user;
              } else {
                throw new Error('User was re-invited but response did not include user ID');
              }
            } else {
              // User exists and is in good state, just update records
              const userId = existingUser.id;
              const primaryTenantId = newUserTenantId;

              // Create approval record and assign role for existing user
              await createUserRecordsForExistingUser(
                userId,
                normalizedEmail,
                newUserFirstName,
                newUserLastName,
                newUserRoleId,
                primaryTenantId,
                newUserNotes,
                user.id,
                adminProdClient
              );

              toast({
                title: 'User Already Exists',
                description: `User ${newUserFullName} already exists. Records updated.`,
                variant: 'default'
              });

              // Reset form and close dialog
              setAddUserDialogOpen(false);
              await loadApprovals();
              return true;
            }
          } else if (!isEmailSendError) {
            // Only throw "user not found" error if this wasn't an email send error
            throw new Error('User already exists but could not be retrieved');
          } else {
            // Email send error but user doesn't exist - some other issue
            console.error('[handleCreateUser] Email send error but user not found in system');
            throw inviteError;
          }
        } else {
          // For other errors, throw them
          console.error(
            '[handleCreateUser] Invite failed with non-existing-user error:',
            inviteError
          );
          throw inviteError;
        }
      }

      // Get user ID from invite response or fetch it
      let userId: string;
      if (resolvedAuthUser?.id) {
        userId = resolvedAuthUser.id;
      } else {
        // Fetch user by email to get ID (fallback if invite response doesn't include user)
        const { data: usersData, error: listError } = await adminAuthClient.auth.admin.listUsers();
        if (listError) throw listError;

        const createdUser = usersData?.users?.find(u => u.email?.toLowerCase() === normalizedEmail);
        if (!createdUser) {
          throw new Error('User was created but could not be retrieved');
        }
        userId = createdUser.id;
      }
      const primaryTenantId = newUserTenantId;

      // Step 2: Create the approval record.
      //
      // This is the step that produced the original "Failed to Create User".
      // prod.user_approvals is UNIQUE (tenant_id, user_id) and the
      // create_user_approval RPC is a bare INSERT with no ON CONFLICT, so
      // re-running this for a user who already has a row raises 23505.
      //
      // Reaching here for an existing user is easy: inviteUserByEmail does
      // NOT error when the target hasn't confirmed yet — GoTrue just re-sends
      // the invite and returns the same user — so the "already exists" branch
      // above never fires for pending users.
      //
      // Check for the row first and update it instead of inserting.
      console.log('[handleCreateUser] Checking for an existing approval record...');
      const { data: existingApproval, error: existingApprovalError } = await adminProdClient
        .from('user_approvals')
        .select('id')
        .eq('user_id', userId)
        .eq('tenant_id', primaryTenantId)
        .maybeSingle();

      if (existingApprovalError) {
        console.warn(
          '[handleCreateUser] Could not check for an existing approval; continuing:',
          existingApprovalError
        );
      }

      if (existingApproval) {
        // Already set up for this tenant — reconcile rather than collide.
        console.log('[handleCreateUser] Approval already exists, updating instead of inserting');
        await createUserRecordsForExistingUser(
          userId,
          normalizedEmail,
          newUserFirstName,
          newUserLastName,
          newUserRoleId,
          primaryTenantId,
          newUserNotes,
          user.id,
          adminProdClient
        );

        setAddUserDialogOpen(false);
        toast({
          title: 'User Updated',
          description: `${newUserFullName} was already set up for this tenant. Their role and details have been updated and a fresh invite sent.`,
          variant: 'success'
        });
        await loadApprovals();
        return true;
      }

      console.log('[handleCreateUser] Creating approval record via RPC...');
      const { data: approvalId, error: approvalError } = await supabase.rpc(
        'create_user_approval',
        {
          p_user_id: userId,
          p_email: normalizedEmail,
          p_full_name: newUserFullName,
          p_tenant_id: primaryTenantId,
          p_status: 'approved',
          p_auth_provider: 'email',
          p_approved_by: user.id,
          // Was hardcoded to null, silently discarding whatever the admin
          // typed into the Notes field.
          p_notes: newUserNotes || null
        }
      );

      if (approvalError) {
        console.error('[handleCreateUser] Error creating approval record:', approvalError);
        // 23505 can still happen if another admin created the same user
        // between the check above and this insert. Say something an admin
        // can act on rather than surfacing the raw constraint name.
        if (approvalError.code === '23505') {
          throw new Error(
            'This user is already set up for that tenant. Refresh the list to see them.'
          );
        }
        throw new Error(
          `Failed to create approval record: ${approvalError.message} (Code: ${approvalError.code || 'unknown'})`
        );
      }
      console.log('[handleCreateUser] Created approval record:', approvalId);

      // Step 3: Assign role via RPC function (bypasses RLS)
      console.log('[handleCreateUser] Assigning role via RPC...');
      const { data: roleAssignmentId, error: roleError } = await supabase.rpc('assign_user_role', {
        p_user_id: userId,
        p_tenant_id: primaryTenantId,
        p_role_id: newUserRoleId,
        p_assigned_by: user.id
      });

      if (roleError) {
        console.error('[handleCreateUser] Error creating role assignment:', roleError);
        throw new Error(
          `Failed to assign role: ${roleError.message} (Code: ${roleError.code || 'unknown'})`
        );
      }
      console.log('[handleCreateUser] Created role assignment:', roleAssignmentId);

      // Step 4: Assign the single tenant.
      // Uses the same helper as the existing-user path — these two branches
      // previously called two different RPCs to do the same job
      // (add_user_to_tenant here vs add_user_tenant_for_admin there), so a
      // fix to one silently missed the other.
      console.log(`[handleCreateUser] Assigning tenant ${primaryTenantId} to user ${userId}...`);
      try {
        const tenantResult = await tenantAPI.addUserTenant(userId, primaryTenantId);
        console.log('[handleCreateUser] Successfully assigned tenant:', tenantResult);
      } catch (tenantError) {
        console.error('[handleCreateUser] Error assigning tenant:', tenantError);
        throw new Error(
          `Failed to assign tenant: ${
            tenantError instanceof Error ? tenantError.message : 'unknown error'
          }`
        );
      }

      // Step 5: Create basic user profile record in prod.user_profiles for profile sync
      // Use check-then-insert/update to avoid 400 errors with composite unique constraints

      try {
        const { data: existingProfile } = await adminProdClient
          .from('user_profiles')
          .select('id')
          .eq('user_id', userId)
          .eq('tenant_id', primaryTenantId)
          .maybeSingle();

        if (existingProfile) {
          // Update existing profile - only use columns that exist in prod.user_profiles
          // Actual columns: id, user_id, tenant_id, profile_photo_path, phone_number, job_title, department, created_at, updated_at
          const { error: profileError } = await adminProdClient
            .from('user_profiles')
            .update({
              updated_at: new Date().toISOString()
            })
            .eq('user_id', userId)
            .eq('tenant_id', primaryTenantId);

          if (profileError) {
            console.warn('Failed to update user profile record:', profileError);
          }
        } else {
          // Insert new profile - only use columns that exist in prod.user_profiles
          // Actual columns: id, user_id, tenant_id, profile_photo_path, phone_number, job_title, department, created_at, updated_at
          const profileData = {
            user_id: userId,
            tenant_id: primaryTenantId
            // Other fields (profile_photo_path, phone_number, job_title, department) are optional
          };

          console.log('[handleCreateUser] Inserting user profile with data:', profileData);
          const { data: profileInsertData, error: profileError } = await adminProdClient
            .from('user_profiles')
            .insert(profileData)
            .select();

          if (profileError) {
            console.error('[handleCreateUser] Failed to create user profile record:', {
              error: profileError,
              message: profileError.message,
              details: profileError.details,
              hint: profileError.hint,
              code: profileError.code,
              data: profileData
            });
            // Don't throw - profile creation is non-blocking
          } else {
            console.log('[handleCreateUser] Successfully created user profile:', profileInsertData);
          }
        }
      } catch (profileError) {
        // Non-blocking - profile creation is optional at this stage
        console.warn('Error creating user profile:', profileError);
      }

      // Step 6: Log audit event via RPC (non-blocking, fire and forget)
      try {
        await supabase.rpc('create_audit_log', {
          p_user_id: user.id,
          p_tenant_id: primaryTenantId,
          p_action: 'user.created',
          p_resource_type: 'user',
          p_resource_id: userId,
          p_old_values: null,
          p_new_values: {
            email: normalizedEmail,
            first_name: newUserFirstName,
            last_name: newUserLastName,
            full_name: newUserFullName,
            auth_provider: 'email',
            role_id: newUserRoleId,
            role_name: roles.find(r => r.id === newUserRoleId)?.display_name,
            tenant_id: newUserTenantId,
            created_via: 'control_center',
            created_by: user.email,
            notes: newUserNotes
          }
        });
      } catch (auditError) {
        // Silently handle audit logging errors - don't fail the operation
        console.warn('Failed to log audit event for user creation:', auditError);
      }

      // Instant feedback pattern: Close dialog and show success immediately
      setAddUserDialogOpen(false);
      toast({
        title: 'User Invited Successfully',
        description: `An invitation email has been sent to ${normalizedEmail}. They will receive a one-time invite link to set their password.`,
        variant: 'success'
      });

      // Refresh approvals list in background
      loadApprovals().catch(err => {
        console.warn('Failed to refresh approvals list:', err);
      });
      return true;
    } catch (error) {
      console.error('[handleCreateUser] Error creating user:', error);

      // Keep the dialog OPEN and the fields populated. Closing it and
      // clearing every input meant a single failure threw away everything
      // the admin had typed and hid the context they needed to fix it.

      const errorMessage =
        error instanceof Error
          ? error.message
          : typeof error === 'object' && error !== null
            ? JSON.stringify(error)
            : 'An unexpected error occurred. Please try again.';

      // Log full error details for debugging
      if (error && typeof error === 'object' && 'code' in error) {
        console.error('[handleCreateUser] Error code:', error.code);
      }
      if (error && typeof error === 'object' && 'details' in error) {
        console.error('[handleCreateUser] Error details:', error.details);
      }
      if (error && typeof error === 'object' && 'hint' in error) {
        console.error('[handleCreateUser] Error hint:', error.hint);
      }

      toast({
        title: 'Failed to Create User',
        description: errorMessage,
        variant: 'destructive'
      });

      // Refresh the list so the duplicate guard sees anything that landed
      // server-side despite the failure.
      loadApprovals().catch(err => {
        console.warn('Failed to refresh approvals list:', err);
      });
      // Keep the dialog open with the admin's values intact so they can
      // correct whatever failed and retry.
      return false;
    } finally {
      setCreatingUser(false);
    }
  };

  // Handle user removal - completely delete user from system
  const handleRemoveUser = async () => {
    if (!userToRemove || !user) return;

    try {
      const adminAuthClient = getAdminAuthClient();
      const adminProdClient = getAdminProdClient();

      // Log audit event BEFORE deletion (so we have the data) via RPC
      const tenantId = userToRemove.user_tenants?.[0]?.id || null;
      try {
        await supabase.rpc('create_audit_log', {
          p_user_id: user.id,
          p_tenant_id: tenantId,
          p_action: 'user.deleted',
          p_resource_type: 'user',
          p_resource_id: userToRemove.user_id,
          p_old_values: {
            status: userToRemove.status,
            role: userToRemove.user_role,
            email: userToRemove.email,
            full_name: userToRemove.full_name,
            auth_provider: userToRemove.auth_provider,
            tenant_ids: userToRemove.user_tenants?.map(t => t.id) || []
          },
          p_new_values: {
            deleted: true,
            deleted_at: new Date().toISOString(),
            deleted_by: user.id,
            deleted_from: 'control_center_user_approvals'
          }
        });
      } catch (auditError) {
        // Log but don't fail - continue with deletion
        console.warn('Failed to log audit event for user deletion:', auditError);
      }

      // Instant feedback: Close dialog immediately and show progress toast
      setRemoveDialogOpen(false);
      toast({
        title: 'Deleting User',
        description: `${userToRemove.email} is being removed from the system...`,
        variant: 'default'
      });

      // Use delete_user_everywhere RPC function via public wrapper - handles all cleanup in one call
      console.log(
        `[handleRemoveUser] Calling delete_user_everywhere for user: ${userToRemove.user_id}`
      );
      const { data: deleteResult, error: deleteError } = await supabase.rpc(
        'delete_user_everywhere',
        {
          p_auth_user_id: userToRemove.user_id,
          p_dry_run: false
        }
      );

      if (deleteError) {
        console.error('[handleRemoveUser] delete_user_everywhere error:', deleteError);
        throw deleteError;
      }

      console.log('[handleRemoveUser] User deletion succeeded:', deleteResult);

      toast({
        title: 'User Deleted',
        description: `${userToRemove.email} has been permanently removed from the system.`,
        variant: 'success'
      });

      // Refresh list to show current state
      loadApprovals().catch(err => console.warn('Failed to refresh approvals:', err));

      setUserToRemove(null);
    } catch (error) {
      console.error('Error during user deletion process:', error);

      // Dialog is already closed from instant feedback pattern
      // Show error toast
      toast({
        title: 'Deletion Error',
        description:
          error instanceof Error
            ? error.message
            : 'An error occurred during user deletion. Some records may have been removed. Please refresh to check the current state.',
        variant: 'destructive'
      });

      // Refresh list to show current state
      loadApprovals().catch(err => console.warn('Failed to refresh approvals:', err));

      setUserToRemove(null);
    }
  };

  // Get user status badge (invited, password set, active)
  const getUserStatus = (approval: UserApprovalRecord) => {
    // If user has logged in, they are active
    if (approval.last_sign_in_at) {
      return {
        status: 'active',
        badge: (
          <Badge className='bg-green-500/20 text-green-700 border-green-500/30 dark:bg-green-500/30 dark:text-green-300 dark:border-green-400/30'>
            <CheckCircle className='h-3 w-3 mr-1' />
            Active
          </Badge>
        )
      };
    }

    // If email is confirmed (password set), but never logged in
    if (approval.email_confirmed_at) {
      return {
        status: 'password_set',
        badge: (
          <Badge className='bg-blue-500/20 text-blue-400 border-blue-500/30 dark:bg-blue-500/30 dark:text-blue-300 dark:border-blue-400/30'>
            <Key className='h-3 w-3 mr-1' />
            Password Set
          </Badge>
        )
      };
    }

    // If invited but no password set
    if (approval.invited_at || approval.status === 'approved') {
      return {
        status: 'invited',
        badge: (
          <Badge className='bg-yellow-500/20 text-yellow-400 border-yellow-500/30 dark:bg-yellow-500/30 dark:text-yellow-300 dark:border-yellow-400/30'>
            <Mail className='h-3 w-3 mr-1' />
            Invited
          </Badge>
        )
      };
    }

    // Default fallback
    return {
      status: 'unknown',
      badge: <Badge variant='outline'>Unknown</Badge>
    };
  };

  // Get email status badge (tracks invite email lifecycle)
  const getEmailStatusBadge = (approval: UserApprovalRecord) => {
    // User has set password (email confirmed)
    if (approval.email_confirmed_at) {
      return (
        <Badge className='bg-green-500/20 text-green-700 border-green-500/30 dark:bg-green-500/30 dark:text-green-300 dark:border-green-400/30'>
          <CheckCircle className='h-3 w-3 mr-1' />
          Password Set
        </Badge>
      );
    }

    // Invite email was sent but not yet acted upon
    if (approval.invited_at || approval.status === 'approved') {
      return (
        <Badge className='bg-amber-500/20 text-amber-600 border-amber-500/30 dark:bg-amber-500/30 dark:text-amber-300 dark:border-amber-400/30'>
          <Mail className='h-3 w-3 mr-1' />
          Email Sent
        </Badge>
      );
    }

    // No invite sent yet (pending approval or just created)
    return (
      <Badge variant='outline' className='text-muted-foreground'>
        <AlertCircle className='h-3 w-3 mr-1' />
        Not Sent
      </Badge>
    );
  };

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get redirect URL based on current origin - works for both dev and production
  const getRedirectUrl = (path: string) => {
    // Always use window.location.origin to ensure correct domain
    // This works for localhost, portal.vehix.ai, or any other deployment
    return `${window.location.origin}${path}`;
  };

  // Send confirmation link (resend invitation)
  // Uses OTP-based flow - email contains 6-digit code, user goes to /invite/confirm
  const handleSendConfirmationLink = async (approval: UserApprovalRecord) => {
    if (!user) return;

    try {
      const adminAuthClient = getAdminAuthClient();

      // Use OTP flow URL - users enter code manually at /invite/confirm
      // This is scanner-safe (email security systems can't consume OTP codes)
      const redirectUrl = `${window.location.origin}/invite/confirm`;

      // Resend invitation email (uses "Invite User" template with {{ .Token }} OTP code)
      const { error: inviteError } = await adminAuthClient.auth.admin.inviteUserByEmail(
        approval.email,
        {
          data: {
            full_name:
              approval.full_name ||
              `${approval.first_name || ''} ${approval.last_name || ''}`.trim(),
            first_name: approval.first_name || '',
            last_name: approval.last_name || ''
          },
          redirectTo: redirectUrl
        }
      );

      if (inviteError) {
        throw inviteError;
      }

      toast({
        title: 'Confirmation Link Sent',
        description: `A confirmation email has been sent to ${approval.email}`,
        variant: 'success'
      });

      // Refresh to update invited_at timestamp
      await loadApprovals();
    } catch (error) {
      console.error('Error sending confirmation link:', error);
      toast({
        title: 'Error Sending Link',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to send confirmation link. Please try again.',
        variant: 'destructive'
      });
    }
  };

  // Send reset password link
  const handleSendResetPasswordLink = async (approval: UserApprovalRecord) => {
    if (!user) return;

    try {
      const adminAuthClient = getAdminAuthClient();

      // Get environment-appropriate redirect URL
      const redirectUrl = getRedirectUrl('/auth/reset-password/confirm');

      // Send reset password email (OTP flow: template uses {{ .Token }}, user enters code at /auth/reset-password/confirm)
      // Uses "Reset password" template; ensure template uses {{ .Token }} and {{ .SiteURL }}/auth/reset-password/confirm
      const { error: resetError } = await adminAuthClient.auth.resetPasswordForEmail(
        approval.email,
        {
          redirectTo: redirectUrl
        }
      );

      if (resetError) {
        throw resetError;
      }

      toast({
        title: 'Reset Password Link Sent',
        description: `A password reset email has been sent to ${approval.email} using the Reset password template.`,
        variant: 'success'
      });
    } catch (error) {
      console.error('Error sending reset password link:', error);
      toast({
        title: 'Error Sending Reset Link',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to send reset password link. Please try again.',
        variant: 'destructive'
      });
    }
  };

  // Revoke user access (reject approval)
  const handleRevokeAccess = async (approval: UserApprovalRecord) => {
    if (!user || !approval) return;

    // Use existing reject logic
    setSelectedApproval(approval);
    setActionType('reject');
    setActionNotes('');
    setActionDialogOpen(true);
  };

  // ====================================================================
  // Confirmation gates for inline edit actions.
  //
  // Each request* function captures the args of an attempted edit, looks
  // up the human-readable display values (role display name, tenant
  // name) for the confirmation dialog, and opens the dialog. The actual
  // mutation (handleRoleChange, handleTenantSwitch, etc.) only runs from
  // the dialog's Confirm button — no API call fires on a misclick.
  // ====================================================================

  const requestRoleChange = (approval: UserApprovalRecord, newRoleId: string) => {
    // Don't open the dialog if the user picked the same role they have
    if (approval.user_role?.role_id === newRoleId) return;
    const newRole = roles.find(r => r.id === newRoleId);
    setPendingRoleChange({
      approval,
      newRoleId,
      oldRoleName:
        approval.user_role?.role_display_name || approval.user_role?.role_name || '— None —',
      newRoleName: newRole?.display_name || newRole?.name || newRoleId
    });
  };

  const requestTenantSwitch = (approval: UserApprovalRecord, newTenantId: string) => {
    // Don't open the dialog if the user picked the same tenant
    const currentTenantId =
      (approval as { tenant_id?: string }).tenant_id || approval.user_tenants?.[0]?.id;
    if (currentTenantId === newTenantId) return;
    const newTenant = (allTenants ?? []).find(t => t.id === newTenantId);
    const oldTenant = approval.user_tenants?.[0];
    setPendingTenantSwitch({
      approval,
      newTenantId,
      oldTenantName: oldTenant?.name || oldTenant?.tenant_name || '— None —',
      newTenantName: newTenant?.tenant_name || newTenant?.name || newTenantId
    });
  };

  const requestResendInvite = (approval: UserApprovalRecord) => {
    setPendingResendInvite(approval);
  };

  const requestResetPassword = (approval: UserApprovalRecord) => {
    setPendingResetPassword(approval);
  };

  if (loading && viewMode === 'users') {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary' />
        <span className='ml-2'>Loading users...</span>
      </div>
    );
  }

  return (
    <div className='flex h-full w-full flex-col overflow-hidden'>
      {/* KPI strip — only shown for the Users view; Audit Log gets the full height */}
      {viewMode === 'users' && (
        <div className='grid grid-cols-2 lg:grid-cols-4 gap-3 px-8 py-4 border-b flex-shrink-0 bg-muted/30'>
          <StatCard
            title='Total Users'
            value={kpiMetrics.totalUsers}
            icon={Users}
            loading={loading}
            description='Across dealer tenants'
          />
          <StatCard
            title='Admins'
            value={kpiMetrics.totalAdmins}
            icon={Shield}
            loading={loading}
            description='Super admins'
          />
          <StatCard
            title='Avg Users / Tenant'
            value={kpiMetrics.avgUsersPerTenant}
            icon={TrendingUp}
            loading={loading || allTenantsLoading}
            description='Active dealer tenants'
          />
          <StatCard
            title='Pending Sign-up'
            value={kpiMetrics.pendingSignup}
            icon={UserPlus}
            loading={loading}
            attention={kpiMetrics.pendingSignup > 5 ? 'warning' : undefined}
            description='Invited, not yet active'
          />
        </div>
      )}

      {/* User lifecycle tab strip — primary scope choice. Sits ABOVE the
          filter toolbar because tabs are about "which set of users am I
          looking at" while the toolbar's search/role/tenant are about
          "filter the current set further". Tab counts derive from the
          full unfiltered approvals list. */}
      {viewMode === 'users' && (
        <div className='px-8 pt-4 pb-0 border-b flex-shrink-0'>
          <Tabs value={userTab} onValueChange={v => setUserTab(v as UserTab)}>
            <TabsList className='h-9'>
              <TabsTrigger value='all' className='gap-2'>
                All
                <span className='text-xs text-muted-foreground tabular-nums'>{tabCounts.all}</span>
              </TabsTrigger>
              <TabsTrigger value='active' className='gap-2'>
                Active
                <span className='text-xs text-muted-foreground tabular-nums'>
                  {tabCounts.active}
                </span>
              </TabsTrigger>
              <TabsTrigger value='pending-signup' className='gap-2'>
                Pending Sign-up
                <span className='text-xs text-muted-foreground tabular-nums'>
                  {tabCounts.pendingSignup}
                </span>
              </TabsTrigger>
              <TabsTrigger value='inactive' className='gap-2'>
                Inactive
                <span className='text-xs text-muted-foreground tabular-nums'>
                  {tabCounts.inactive}
                </span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      )}

      {/* Compact toolbar — filters + view toggle + actions in one row */}
      <div className='flex flex-wrap items-end gap-4 px-8 py-4 border-b flex-shrink-0'>
        {viewMode === 'users' && (
          <>
            <div className='space-y-1 flex-1 min-w-[220px] max-w-md'>
              <label className='text-xs font-medium text-muted-foreground'>Search</label>
              <div className='relative'>
                <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                <Input
                  placeholder='Search by email or name...'
                  className='pl-9 h-9'
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className='space-y-1'>
              <label className='text-xs font-medium text-muted-foreground'>Role</label>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className='w-[160px] h-9'>
                  <SelectValue placeholder='All Roles' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>All Roles</SelectItem>
                  <SelectItem value='no_role'>No Role Assigned</SelectItem>
                  {roles.map(role => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className='space-y-1'>
              <label className='text-xs font-medium text-muted-foreground'>Tenant</label>
              <Select
                value={tenantFilter && tenantFilter.trim() !== '' ? tenantFilter : undefined}
                onValueChange={setTenantFilter}
              >
                <SelectTrigger className='w-[180px] h-9'>
                  <SelectValue placeholder='All Tenants' />
                </SelectTrigger>
                <SelectContent className='z-[10000]'>
                  <SelectItem value='all'>All Tenants</SelectItem>
                  {allTenants?.map(tenant => (
                    <SelectItem key={tenant.id} value={tenant.id}>
                      {tenant.tenant_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        )}

        <div className='flex-1' />

        {viewMode === 'users' && (
          <span className='text-xs text-muted-foreground self-end pb-2.5'>
            {filteredApprovals.length.toLocaleString()} user
            {filteredApprovals.length === 1 ? '' : 's'}
          </span>
        )}

        {/* View toggle — icon-only segmented control */}
        <div className='flex items-center bg-muted p-1 rounded-md h-9 self-end'>
          <Button
            variant={viewMode === 'users' ? 'secondary' : 'ghost'}
            size='sm'
            className='h-7 w-7 p-0'
            onClick={() => setViewMode('users')}
            title='Users'
            aria-label='Users view'
          >
            <Users className='h-4 w-4' />
          </Button>
          <Button
            variant={viewMode === 'audit' ? 'secondary' : 'ghost'}
            size='sm'
            className='h-7 w-7 p-0'
            onClick={() => setViewMode('audit')}
            title='Audit Log'
            aria-label='Audit log view'
          >
            <FileText className='h-4 w-4' />
          </Button>
        </div>

        <Button
          variant='ghost'
          size='sm'
          className='h-9 w-9 p-0 self-end'
          onClick={loadApprovals}
          title='Refresh'
          aria-label='Refresh users'
        >
          <RefreshCcw className='h-4 w-4' />
        </Button>

        {viewMode === 'users' && (
          <Button onClick={() => setAddUserDialogOpen(true)} className='h-9 self-end'>
            <UserIcon className='h-4 w-4 mr-1' />
            Add User
          </Button>
        )}
      </div>

      {/* Content area */}
      <div className='flex-1 min-h-0 overflow-y-auto px-8 py-6'>
        {viewMode === 'audit' ? (
          <UserAuditTab />
        ) : (
          <>
            {/* Table */}
            <div className='border rounded-lg bg-card overflow-hidden'>
              <div className='overflow-x-auto'>
                <Table className='min-w-[1200px]'>
                  <TableHeader>
                    <TableRow>
                      <TableHead className='w-12 text-center'>#</TableHead>
                      <TableHead>Full Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Email Status</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Tenant</TableHead>
                      <TableHead>Added On</TableHead>
                      <TableHead>Last Login</TableHead>
                      <TableHead className='text-right'>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedApprovals.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} className='text-center py-12 text-muted-foreground'>
                          No user approvals found
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedApprovals.map((approval, idx) => {
                        const rowNumber = startIndex + idx + 1;
                        const userStatus = getUserStatus(approval);
                        const displayFirstName =
                          approval.first_name || approval.full_name?.split(' ')[0] || '-';
                        const displayLastName =
                          approval.last_name ||
                          approval.full_name?.split(' ').slice(1).join(' ') ||
                          '';
                        const displayFullName = displayLastName
                          ? `${displayFirstName} ${displayLastName}`.trim()
                          : displayFirstName;

                        return (
                          <TableRow key={approval.id}>
                            <TableCell className='text-center text-xs text-muted-foreground tabular-nums'>
                              {rowNumber}
                            </TableCell>
                            <TableCell>
                              <div className='flex items-center gap-2'>
                                <div className='h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center'>
                                  <UserIcon className='h-4 w-4 text-primary' />
                                </div>
                                <div>
                                  <div className='font-medium'>{displayFullName}</div>
                                  {displayFirstName && displayLastName && (
                                    <div className='text-xs text-muted-foreground'>
                                      {displayFirstName} {displayLastName}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className='flex items-center gap-2'>
                                <Mail className='h-4 w-4 text-muted-foreground' />
                                <span>{approval.email}</span>
                              </div>
                            </TableCell>
                            <TableCell>{userStatus.badge}</TableCell>
                            <TableCell>{getEmailStatusBadge(approval)}</TableCell>
                            <TableCell>
                              {approval.status === 'approved' ? (
                                <Select
                                  value={approval.user_role?.role_id || ''}
                                  onValueChange={newRoleId =>
                                    requestRoleChange(approval, newRoleId)
                                  }
                                >
                                  <SelectTrigger className='w-[180px] h-8'>
                                    <SelectValue placeholder='No role assigned'>
                                      {approval.user_role ? (
                                        <div className='flex items-center gap-1'>
                                          <Shield className='h-3 w-3' />
                                          <span>{approval.user_role.role_display_name}</span>
                                        </div>
                                      ) : (
                                        <span className='text-muted-foreground'>No role</span>
                                      )}
                                    </SelectValue>
                                  </SelectTrigger>
                                  <SelectContent>
                                    {roles.map(role => (
                                      <SelectItem key={role.id} value={role.id}>
                                        <div className='flex flex-col'>
                                          <span className='font-medium'>{role.display_name}</span>
                                          {role.description && (
                                            <span className='text-xs text-muted-foreground'>
                                              {role.description}
                                            </span>
                                          )}
                                        </div>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : approval.user_role ? (
                                <Badge variant='outline' className='capitalize'>
                                  <Shield className='h-3 w-3 mr-1' />
                                  {approval.user_role.role_display_name}
                                </Badge>
                              ) : (
                                <span className='text-sm text-muted-foreground'>-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {approval.status === 'approved' ? (
                                allTenantsLoading ? (
                                  <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                                    <div className='animate-spin rounded-full h-3 w-3 border-2 border-primary border-t-transparent' />
                                    Loading...
                                  </div>
                                ) : allTenantsError ? (
                                  <span className='text-sm text-destructive'>
                                    Error loading tenants
                                  </span>
                                ) : allTenants && allTenants.length > 0 ? (
                                  <Select
                                    value={
                                      approval.tenant_id || approval.user_tenants?.[0]?.id || ''
                                    }
                                    onValueChange={value => requestTenantSwitch(approval, value)}
                                  >
                                    <SelectTrigger className='w-[220px] h-8'>
                                      <SelectValue placeholder='Select tenant'>
                                        {approval.user_tenants?.[0] ? (
                                          <div className='flex items-center gap-1'>
                                            <Building2 className='h-3 w-3' />
                                            <span>
                                              {approval.user_tenants[0].name ||
                                                approval.user_tenants[0].tenant_name}
                                            </span>
                                          </div>
                                        ) : (
                                          <span className='text-muted-foreground'>No tenant</span>
                                        )}
                                      </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent>
                                      {allTenants.map(tenant => (
                                        <SelectItem key={tenant.id} value={tenant.id}>
                                          <div className='flex items-center gap-2'>
                                            <Building2 className='h-3 w-3 text-muted-foreground' />
                                            <span>{tenant.name || tenant.tenant_name}</span>
                                          </div>
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <span className='text-sm text-muted-foreground'>No tenants</span>
                                )
                              ) : (
                                <span className='text-sm text-muted-foreground'>-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                                <Calendar className='h-4 w-4' />
                                {formatDate(approval.approved_at)}
                              </div>
                            </TableCell>
                            <TableCell>
                              {approval.last_sign_in_at ? (
                                <div className='text-sm text-muted-foreground'>
                                  {formatDate(approval.last_sign_in_at)}
                                </div>
                              ) : (
                                <span className='text-sm text-muted-foreground'>Never</span>
                              )}
                            </TableCell>
                            <TableCell className='text-right'>
                              <div className='flex items-center justify-end gap-2'>
                                {approval.status === 'pending' && (
                                  <>
                                    <Button
                                      size='sm'
                                      variant='default'
                                      className='bg-green-600 hover:bg-green-700'
                                      onClick={() => openActionDialog(approval, 'approve')}
                                    >
                                      <CheckCircle className='h-4 w-4 mr-1' />
                                      Approve
                                    </Button>
                                    <Button
                                      size='sm'
                                      variant='outline'
                                      className='border-red-200 text-red-600 hover:bg-red-50'
                                      onClick={() => openActionDialog(approval, 'reject')}
                                    >
                                      <XCircle className='h-4 w-4 mr-1' />
                                      Reject
                                    </Button>
                                  </>
                                )}
                                {(approval.status === 'approved' ||
                                  approval.status === 'rejected') && (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant='ghost'
                                        size='icon'
                                        className='h-8 w-8'
                                        title='More actions'
                                      >
                                        <MoreVertical className='h-4 w-4' />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align='end' className='w-56'>
                                      <DropdownMenuItem
                                        onClick={() => requestResendInvite(approval)}
                                        className='cursor-pointer'
                                      >
                                        <Mail className='h-4 w-4 mr-2' />
                                        Send Confirmation Link
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        onClick={() => requestResetPassword(approval)}
                                        className='cursor-pointer'
                                      >
                                        <Key className='h-4 w-4 mr-2' />
                                        Send Reset Password Link
                                      </DropdownMenuItem>
                                      {approval.status === 'approved' && (
                                        <DropdownMenuItem
                                          onClick={() => handleRevokeAccess(approval)}
                                          className='cursor-pointer text-orange-600 dark:text-orange-400 focus:text-orange-600 dark:focus:text-orange-400 focus:bg-orange-50 dark:focus:bg-orange-500/10'
                                        >
                                          <Ban className='h-4 w-4 mr-2' />
                                          Revoke Access
                                        </DropdownMenuItem>
                                      )}
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem
                                        onClick={() => openRemoveDialog(approval)}
                                        className='cursor-pointer text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400 focus:bg-red-50 dark:focus:bg-red-500/10'
                                      >
                                        <Trash2 className='h-4 w-4 mr-2' />
                                        Remove User
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Pagination Controls */}
            {viewMode === 'users' && filteredApprovals.length > 0 && (
              <div className='flex items-center justify-between mt-4'>
                <div className='flex items-center gap-2'>
                  <span className='text-sm text-muted-foreground'>Rows per page:</span>
                  <Select
                    value={pageSize.toString()}
                    onValueChange={value => {
                      setPageSize(Number(value));
                      setCurrentPage(1);
                    }}
                  >
                    <SelectTrigger className='w-16 h-8 text-sm'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='5'>5</SelectItem>
                      <SelectItem value='10'>10</SelectItem>
                      <SelectItem value='25'>25</SelectItem>
                      <SelectItem value='50'>50</SelectItem>
                      <SelectItem value='100'>100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className='flex items-center gap-2'>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className='hidden sm:inline-flex'
                  >
                    First
                  </Button>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>

                  <div className='flex items-center gap-1'>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? 'default' : 'outline'}
                          size='sm'
                          onClick={() => setCurrentPage(pageNum)}
                          className='w-8 h-8 p-0'
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>

                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className='hidden sm:inline-flex'
                  >
                    Last
                  </Button>
                </div>
              </div>
            )}

            {/* Last Refresh Indicator - Show most recent approval time */}
            <div className='flex justify-center pt-4'>
              {approvals.length > 0 ? (
                <div className='text-sm text-muted-foreground italic'>
                  {(() => {
                    const timestamps = approvals
                      .map(a => {
                        const requested = a.requested_at ? new Date(a.requested_at).getTime() : 0;
                        const approved = a.approved_at ? new Date(a.approved_at).getTime() : 0;
                        return Math.max(requested, approved);
                      })
                      .filter(ts => ts > 0); // Filter out invalid dates

                    if (timestamps.length === 0) {
                      return 'No valid approval dates';
                    }

                    const mostRecent = Math.max(...timestamps);
                    return `Most recent approval: ${new Date(mostRecent).toLocaleString()}`;
                  })()}
                </div>
              ) : (
                <div className='text-sm text-muted-foreground italic'>No approvals loaded</div>
              )}
            </div>

            {/* Action Dialog */}
            <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {actionType === 'approve' ? (
                      <span className='flex items-center gap-2 text-green-600'>
                        <CheckCircle className='h-5 w-5' />
                        Approve User Access
                      </span>
                    ) : (
                      <span className='flex items-center gap-2 text-red-600'>
                        <XCircle className='h-5 w-5' />
                        Reject User Access
                      </span>
                    )}
                  </DialogTitle>
                  <DialogDescription>
                    {actionType === 'approve'
                      ? `Grant ${selectedApproval?.email} access to the application.`
                      : `Deny ${selectedApproval?.email} access to the application.`}
                  </DialogDescription>
                </DialogHeader>

                <div className='space-y-4 py-4'>
                  {/* User Info */}
                  <div className='bg-muted/50 rounded-lg p-4 space-y-2'>
                    <div className='flex items-center gap-2'>
                      <Mail className='h-4 w-4 text-muted-foreground' />
                      <span className='text-sm font-medium'>{selectedApproval?.email}</span>
                    </div>
                    <div className='flex items-center gap-2'>
                      <UserIcon className='h-4 w-4 text-muted-foreground' />
                      <span className='text-sm'>
                        {selectedApproval?.full_name || 'No name provided'}
                      </span>
                    </div>
                    <div className='flex items-center gap-2'>
                      <Shield className='h-4 w-4 text-muted-foreground' />
                      <span className='text-sm capitalize'>{selectedApproval?.auth_provider}</span>
                    </div>
                    <div className='flex items-center gap-2'>
                      <Calendar className='h-4 w-4 text-muted-foreground' />
                      <span className='text-sm'>
                        Requested: {formatDate(selectedApproval?.requested_at || null)}
                      </span>
                    </div>
                  </div>

                  {/* Role Selection - only for approve action */}
                  {actionType === 'approve' && (
                    <div className='space-y-2'>
                      <label className='text-sm font-medium text-red-600'>Assign Role *</label>
                      <Select
                        value={
                          selectedRoleId && selectedRoleId.trim() !== ''
                            ? selectedRoleId
                            : undefined
                        }
                        onValueChange={value => setSelectedRoleId(value)}
                        required
                      >
                        <SelectTrigger className='w-full'>
                          <SelectValue
                            placeholder={
                              roles.length === 0
                                ? 'Loading roles...'
                                : 'Select a role for this user...'
                            }
                          />
                        </SelectTrigger>
                        <SelectContent className='z-[10000]'>
                          {roles.length === 0 ? (
                            <SelectItem value='loading' disabled>
                              Loading roles...
                            </SelectItem>
                          ) : (
                            roles.map(role => (
                              <SelectItem key={role.id} value={role.id}>
                                <div className='flex flex-col'>
                                  <span className='font-medium'>{role.display_name}</span>
                                  {role.description && (
                                    <span className='text-xs text-muted-foreground'>
                                      {role.description}
                                    </span>
                                  )}
                                </div>
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <p className='text-xs text-muted-foreground'>
                        This determines what permissions and access the user will have in the
                        system.
                      </p>
                    </div>
                  )}

                  {/* Notes */}
                  <div className='space-y-2'>
                    <label className='text-sm font-medium'>
                      {actionType === 'reject' ? 'Reason (Required)' : 'Notes (Optional)'}
                    </label>
                    <Textarea
                      placeholder={
                        actionType === 'reject'
                          ? 'Explain why access is being denied...'
                          : 'Add any additional notes...'
                      }
                      value={actionNotes}
                      onChange={e => setActionNotes(e.target.value)}
                      rows={3}
                      required={actionType === 'reject'}
                    />
                  </div>
                </div>

                <DialogFooter>
                  <Button variant='outline' onClick={() => setActionDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    variant={actionType === 'approve' ? 'default' : 'destructive'}
                    onClick={handleAction}
                    disabled={
                      (actionType === 'approve' && !selectedRoleId) ||
                      (actionType === 'reject' && !actionNotes.trim())
                    }
                    className={actionType === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}
                  >
                    {actionType === 'approve' ? (
                      <>
                        <CheckCircle className='h-4 w-4 mr-2' />
                        Approve User
                      </>
                    ) : (
                      <>
                        <XCircle className='h-4 w-4 mr-2' />
                        Reject User
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Remove User Dialog */}
            <Dialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    <span className='flex items-center gap-2 text-red-600'>
                      <Trash2 className='h-5 w-5' />
                      Remove User
                    </span>
                  </DialogTitle>
                  <DialogDescription>
                    This action will permanently delete the user from the system. This cannot be
                    undone.
                  </DialogDescription>
                </DialogHeader>

                <div className='space-y-4 py-4'>
                  {/* User Info */}
                  <div className='bg-muted/50 rounded-lg p-4 space-y-2'>
                    <div className='flex items-center gap-2'>
                      <Mail className='h-4 w-4 text-muted-foreground' />
                      <span className='text-sm font-medium'>{userToRemove?.email}</span>
                    </div>
                    <div className='flex items-center gap-2'>
                      <UserIcon className='h-4 w-4 text-muted-foreground' />
                      <span className='text-sm'>
                        {userToRemove?.full_name || 'No name provided'}
                      </span>
                    </div>
                    {userToRemove?.user_role && (
                      <div className='flex items-center gap-2'>
                        <Shield className='h-4 w-4 text-muted-foreground' />
                        <span className='text-sm'>{userToRemove.user_role.role_display_name}</span>
                      </div>
                    )}
                  </div>

                  {/* Warning */}
                  <div className='bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900 rounded-lg p-4'>
                    <p className='text-sm text-red-800 dark:text-red-200'>
                      <strong>Warning:</strong> This will permanently delete the user from the
                      system. system. This action cannot be undone and will:
                    </p>
                    <ul className='text-sm text-red-800 dark:text-red-200 mt-2 ml-4 list-disc'>
                      <li>Delete the user from Supabase Auth</li>
                      <li>Remove all role assignments</li>
                      <li>Remove all tenant associations</li>
                      <li>Delete approval records</li>
                      <li>Delete user profile data</li>
                    </ul>
                    <p className='text-sm text-red-800 dark:text-red-200 mt-2'>
                      This action will be logged in the audit trail.
                    </p>
                  </div>
                </div>

                <DialogFooter>
                  <Button variant='outline' onClick={() => setRemoveDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button variant='destructive' onClick={handleRemoveUser}>
                    <Trash2 className='h-4 w-4 mr-2' />
                    Remove User
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Confirm Role Change — protects against accidental Select clicks
              that would silently change a user's access scope. */}
            <Dialog
              open={!!pendingRoleChange}
              onOpenChange={open => {
                if (!open) {
                  setPendingRoleChange(null);
                }
              }}
            >
              <DialogContent className='max-w-md'>
                <DialogHeader>
                  <DialogTitle className='flex items-center gap-2'>
                    <Shield className='h-5 w-5 text-primary' />
                    Change role?
                  </DialogTitle>
                  <DialogDescription>
                    This changes what{' '}
                    <span className='font-semibold text-foreground'>
                      {pendingRoleChange?.approval.email}
                    </span>{' '}
                    can do in the app.
                  </DialogDescription>
                </DialogHeader>
                {pendingRoleChange && (
                  <div className='rounded-md border bg-muted/30 p-3 space-y-1 text-sm'>
                    <div className='flex justify-between gap-3'>
                      <span className='text-muted-foreground'>From</span>
                      <span className='font-medium'>{pendingRoleChange.oldRoleName}</span>
                    </div>
                    <div className='flex justify-between gap-3'>
                      <span className='text-muted-foreground'>To</span>
                      <span className='font-medium text-primary'>
                        {pendingRoleChange.newRoleName}
                      </span>
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button
                    variant='outline'
                    onClick={() => setPendingRoleChange(null)}
                    disabled={confirmActionLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={async () => {
                      if (!pendingRoleChange) return;
                      setConfirmActionLoading(true);
                      try {
                        await handleRoleChange(
                          pendingRoleChange.approval,
                          pendingRoleChange.newRoleId
                        );
                      } finally {
                        setConfirmActionLoading(false);
                        setPendingRoleChange(null);
                      }
                    }}
                    disabled={confirmActionLoading}
                  >
                    {confirmActionLoading && <Loader2 className='h-4 w-4 mr-2 animate-spin' />}
                    Change role
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Confirm Tenant Switch — high-impact, reassigns the user's data scope. */}
            <Dialog
              open={!!pendingTenantSwitch}
              onOpenChange={open => {
                if (!open) {
                  setPendingTenantSwitch(null);
                }
              }}
            >
              <DialogContent className='max-w-md'>
                <DialogHeader>
                  <DialogTitle className='flex items-center gap-2'>
                    <Building2 className='h-5 w-5 text-primary' />
                    Move user to a different tenant?
                  </DialogTitle>
                  <DialogDescription>
                    <span className='font-semibold text-foreground'>
                      {pendingTenantSwitch?.approval.email}
                    </span>
                    's data scope will change immediately on save.
                  </DialogDescription>
                </DialogHeader>
                {pendingTenantSwitch && (
                  <div className='rounded-md border bg-muted/30 p-3 space-y-1 text-sm'>
                    <div className='flex justify-between gap-3'>
                      <span className='text-muted-foreground'>From</span>
                      <span className='font-medium'>{pendingTenantSwitch.oldTenantName}</span>
                    </div>
                    <div className='flex justify-between gap-3'>
                      <span className='text-muted-foreground'>To</span>
                      <span className='font-medium text-primary'>
                        {pendingTenantSwitch.newTenantName}
                      </span>
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button
                    variant='outline'
                    onClick={() => setPendingTenantSwitch(null)}
                    disabled={confirmActionLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={async () => {
                      if (!pendingTenantSwitch) return;
                      setConfirmActionLoading(true);
                      try {
                        await handleTenantSwitch(
                          pendingTenantSwitch.approval,
                          pendingTenantSwitch.newTenantId
                        );
                      } finally {
                        setConfirmActionLoading(false);
                        setPendingTenantSwitch(null);
                      }
                    }}
                    disabled={confirmActionLoading}
                  >
                    {confirmActionLoading && <Loader2 className='h-4 w-4 mr-2 animate-spin' />}
                    Move user
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Confirm Resend Invite — sends a confirmation email; lower-stakes
              than role/tenant changes but still defensive against misclicks. */}
            <Dialog
              open={!!pendingResendInvite}
              onOpenChange={open => {
                if (!open) {
                  setPendingResendInvite(null);
                }
              }}
            >
              <DialogContent className='max-w-md'>
                <DialogHeader>
                  <DialogTitle className='flex items-center gap-2'>
                    <Mail className='h-5 w-5 text-primary' />
                    Send a fresh confirmation link?
                  </DialogTitle>
                  <DialogDescription>
                    An email with a new password-setup link will be sent to{' '}
                    <span className='font-semibold text-foreground'>
                      {pendingResendInvite?.email}
                    </span>
                    .
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button
                    variant='outline'
                    onClick={() => setPendingResendInvite(null)}
                    disabled={confirmActionLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={async () => {
                      if (!pendingResendInvite) return;
                      setConfirmActionLoading(true);
                      try {
                        await handleSendConfirmationLink(pendingResendInvite);
                      } finally {
                        setConfirmActionLoading(false);
                        setPendingResendInvite(null);
                      }
                    }}
                    disabled={confirmActionLoading}
                  >
                    {confirmActionLoading && <Loader2 className='h-4 w-4 mr-2 animate-spin' />}
                    Send invite
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Confirm Reset Password — sends a reset email; doesn't change the
              password until the user clicks the link, but the recipient gets
              an unexpected email if clicked accidentally. */}
            <Dialog
              open={!!pendingResetPassword}
              onOpenChange={open => {
                if (!open) {
                  setPendingResetPassword(null);
                }
              }}
            >
              <DialogContent className='max-w-md'>
                <DialogHeader>
                  <DialogTitle className='flex items-center gap-2'>
                    <Key className='h-5 w-5 text-primary' />
                    Send a password reset email?
                  </DialogTitle>
                  <DialogDescription>
                    <span className='font-semibold text-foreground'>
                      {pendingResetPassword?.email}
                    </span>{' '}
                    will receive a reset link. Their current password keeps working until they click
                    the link and set a new one.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button
                    variant='outline'
                    onClick={() => setPendingResetPassword(null)}
                    disabled={confirmActionLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={async () => {
                      if (!pendingResetPassword) return;
                      setConfirmActionLoading(true);
                      try {
                        await handleSendResetPasswordLink(pendingResetPassword);
                      } finally {
                        setConfirmActionLoading(false);
                        setPendingResetPassword(null);
                      }
                    }}
                    disabled={confirmActionLoading}
                  >
                    {confirmActionLoading && <Loader2 className='h-4 w-4 mr-2 animate-spin' />}
                    Send reset email
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Add New User Dialog.
                Extracted into its own component so that typing in the form
                re-renders only the dialog, not this page's user table (up to
                100 rows, each with two Radix Selects and a dropdown menu). */}
            <AddUserDialog
              open={addUserDialogOpen}
              onOpenChange={setAddUserDialogOpen}
              roles={roles}
              rolesError={rolesError}
              allTenants={allTenants}
              allTenantsLoading={allTenantsLoading}
              allTenantsError={allTenantsError}
              approvals={approvals}
              currentUserEmail={user?.email}
              creating={creatingUser}
              onCreate={handleCreateUser}
              onResendInvite={requestResendInvite}
              onViewUser={setSelectedApproval}
            />
          </>
        )}
      </div>
    </div>
  );
};
